import {
    isItemElegivelParaReversao,
    IUploadDocumentoItem,
} from '../../../models/upload-documentos-em-lote.service.model';

/**
 * Tabela de colaboradores do lote, usada nas seções do detalhe. A busca é
 * client-side e o "selecionar todos" atua sobre os itens visíveis no filtro,
 * preservando seleções fora dele.
 */
export class ColaboradoresResultadoTableController {
    private static nextInstanceId = 0;

    public items: IUploadDocumentoItem[] = [];
    public showSelection = false;
    public selectedItems: IUploadDocumentoItem[] = [];
    public onSelectionChange?: ({ selectedItems }: { selectedItems: IUploadDocumentoItem[] }) => void;

    public filteredItems: IUploadDocumentoItem[] = [];
    public searchTerm = '';
    public instanceId = `colaboradores_upload_lote_table_${ColaboradoresResultadoTableController.nextInstanceId++}`;

    public selectedMap: { [trabalhador: string]: boolean } = {};

    $onInit() {
        this.selectedItems = this.selectedItems || [];
        this.applySearchFilter();
        this.syncSelectedMap();
    }

    $onChanges() {
        this.selectedItems = this.selectedItems || [];
        this.applySearchFilter();
        this.syncSelectedMap();
    }

    hasItems() {
        return !!this.items?.length;
    }

    isSelectable(item: IUploadDocumentoItem) {
        return isItemElegivelParaReversao(item);
    }

    search() {
        this.applySearchFilter();
    }

    hasFilteredItems() {
        return !!this.filteredItems.length;
    }

    hasSearchTerm() {
        return !!this.normalizeSearchValue(this.searchTerm);
    }

    hasItensSelecionaveis() {
        return this.getItensSelecionaveis().length > 0;
    }

    temTodosSelecionados() {
        const itensSelecionaveis = this.getItensSelecionaveis();

        return !!itensSelecionaveis.length
            && itensSelecionaveis.every((item) => !!this.selectedMap[item.trabalhador]);
    }

    temSelecaoParcial() {
        const itensSelecionaveis = this.getItensSelecionaveis();

        return itensSelecionaveis.some((item) => !!this.selectedMap[item.trabalhador])
            && !this.temTodosSelecionados();
    }

    /**
     * Chamado pelo ng-change do checkbox, que usa ng-model sobre o selectedMap: o
     * mapa ja carrega o novo valor aqui, entao `true` significa recem-marcado.
     */
    toggleSelection(item: IUploadDocumentoItem) {
        if (!this.isSelectable(item)) {
            return;
        }

        const isSelected = !!this.selectedMap[item.trabalhador];
        const semOItem = this.selectedItems.filter((selected) => selected.trabalhador !== item.trabalhador);

        this.selectedItems = isSelected ? [...semOItem, item] : semOItem;

        this.emitSelectionChange();
    }

    toggleTodos() {
        const itensSelecionaveis = this.getItensSelecionaveis();
        const idsFiltrados = new Set((this.filteredItems || []).map((item) => item.trabalhador));

        // preserva o que estava selecionado fora do filtro atual.
        const itensMantidos = this.selectedItems.filter((item) => !idsFiltrados.has(item.trabalhador));

        if (!itensSelecionaveis.length) {
            return;
        }

        this.selectedItems = this.temTodosSelecionados()
            ? itensMantidos
            : this.mergeSelectedItems(itensMantidos, itensSelecionaveis);

        this.emitSelectionChange();
    }

    private getItensSelecionaveis() {
        return this.filteredItems.filter((item) => this.isSelectable(item));
    }

    private applySearchFilter() {
        const termo = this.normalizeSearchValue(this.searchTerm);
        const itens = this.items || [];

        this.filteredItems = !termo
            ? [...itens]
            : itens.filter((item) => (
                this.normalizeSearchValue(item.nome).includes(termo)
                || this.normalizeSearchValue(item.codigo).includes(termo)
            ));
    }

    private mergeSelectedItems(...grupos: IUploadDocumentoItem[][]) {
        const mapa = new Map<string, IUploadDocumentoItem>();

        grupos.forEach((grupo) => {
            grupo.forEach((item) => {
                mapa.set(item.trabalhador, item);
            });
        });

        return Array.from(mapa.values());
    }

    /**
     * Define uma chave para cada item, e nao so para os selecionados: o ng-model
     * dos checkboxes le este mapa e chave ausente o deixa indefinido.
     */
    private syncSelectedMap() {
        const selecionados = new Set(this.selectedItems.map((item) => item.trabalhador));

        this.selectedMap = (this.items || []).reduce((acc, item) => {
            acc[item.trabalhador] = selecionados.has(item.trabalhador);

            return acc;
        }, {} as { [trabalhador: string]: boolean });
    }

    private emitSelectionChange() {
        this.syncSelectedMap();

        if (this.onSelectionChange) {
            this.onSelectionChange({ selectedItems: this.selectedItems });
        }
    }

    private normalizeSearchValue(value?: string) {
        return (value || '')
            .toString()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .toLowerCase();
    }
}
